import { useEffect, useMemo, useState } from "react";
import "../styles/invoice.css";
import logo from "../assets/logo.png";
import { generateInvoicePDF } from "../utils/generateInvoicePDF";
import { API_BASE_URL } from "../config/api";
import { INVOICE_BRAND } from "../config/invoiceBrand";

export default function InvoicePreview({ invoice }) {
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [routeImageFailed, setRouteImageFailed] = useState(false);
  const [editableBreakdown, setEditableBreakdown] = useState([]);

  const format = (v) =>
    `R ${Number(v || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
    })}`;

  const formatDate = (value) => {
    const parsed = value ? new Date(value) : new Date();
    if (Number.isNaN(parsed.getTime())) return new Date().toLocaleDateString();
    return parsed.toLocaleDateString();
  };
  const issueDate = useMemo(
    () => new Date(invoice?.date || new Date()),
    [invoice]
  );
  const validityDate = useMemo(() => {
    const d = new Date(issueDate);
    d.setDate(d.getDate() + INVOICE_BRAND.validityDays);
    return d;
  }, [issueDate]);

  /* ================= NORMALIZE ================= */
  const breakdown = useMemo(() => {
    const fallback = [
      { label: "Fuel", value: invoice?.pricing?.fuelCost },
      { label: "Transport", value: invoice?.pricing?.transportCost },
      { label: "Tolls", value: invoice?.pricing?.tollCost },
      { label: "Escort", value: invoice?.pricing?.escortCost },
    ];

    const source =
      Array.isArray(invoice?.breakdown) && invoice.breakdown.length > 0
        ? invoice.breakdown
        : fallback;

    return source
      .map((item) => ({
        label: item?.label || "Cost Item",
        value: Number(item?.value) || 0
      }))
      .filter((item) => item.value > 0);
  }, [invoice]);

  const total = useMemo(
    () =>
      Number(
        invoice?.total ||
          invoice?.summary?.total ||
          invoice?.pricing?.totalCost ||
          breakdown.reduce((sum, item) => sum + item.value, 0)
      ) || 0,
    [invoice, breakdown]
  );
  useEffect(() => {
    setEditableBreakdown(
      breakdown.map((item) => ({
        ...item,
        value: Number(item.value) || 0
      }))
    );
  }, [breakdown]);
  const editedTotal = useMemo(
    () => editableBreakdown.reduce((sum, item) => sum + (Number(item.value) || 0), 0),
    [editableBreakdown]
  );

  const load = useMemo(() => {
    const toNumber = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    return {
      weight: toNumber(
        invoice?.loadDetails?.weight ??
          invoice?.form?.weight ??
          invoice?.summary?.load?.weight ??
          invoice?.load?.weight
      ),
      width: toNumber(
        invoice?.loadDetails?.width ??
          invoice?.form?.width ??
          invoice?.summary?.load?.width ??
          invoice?.load?.width
      ),
      height: toNumber(
        invoice?.loadDetails?.height ??
          invoice?.form?.height ??
          invoice?.summary?.load?.height ??
          invoice?.load?.height
      ),
      machineType:
        invoice?.loadDetails?.machineType ||
        invoice?.form?.machineDetails?.machineType ||
        invoice?.load?.machineType ||
        "",
      machineCategory:
        invoice?.loadDetails?.machineCategory ||
        invoice?.form?.machineDetails?.machineCategory ||
        invoice?.load?.machineCategory ||
        "",
      trailerType:
        invoice?.loadDetails?.trailerType ||
        invoice?.form?.machineDetails?.trailerType ||
        invoice?.load?.trailerType ||
        "",
      axleConfiguration:
        invoice?.loadDetails?.axleConfiguration ||
        invoice?.form?.machineDetails?.axleConfiguration ||
        invoice?.load?.axleConfiguration ||
        "",
      classification:
        invoice?.loadDetails?.classification ||
        invoice?.risk?.classification ||
        invoice?.summary?.classification ||
        "STANDARD"
    };
  }, [invoice]);

  const client = useMemo(() => {
    const details = invoice?.client || {};
    const formClient = invoice?.form?.client || {};
    const isPlaceholder = (value) =>
      ["client name", "client company", "client@email.com"].includes(
        String(value || "").trim().toLowerCase()
      );
    const pick = (...values) =>
      values.find((value) => {
        const normalized = String(value || "").trim();
        return normalized && !isPlaceholder(normalized);
      }) || "";

    return {
      name: pick(details.name, formClient.name) || "Client Name",
      company: pick(details.company, formClient.company) || "-",
      email: pick(details.email, details.contact, formClient.email) || "-",
      phone: pick(details.phone, formClient.phone) || "-"
    };
  }, [invoice]);

  const riskAssessment = useMemo(() => {
    const details = invoice?.riskAssessment || invoice?.risk || {};
    return {
      classification: details.classification || load.classification || "Unknown",
      riskLevel: details.riskLevel || "unknown",
      score: Number(details.score || 0),
      reasons: Array.isArray(details.reasons) ? details.reasons : [],
      recommendations: Array.isArray(details.recommendations) ? details.recommendations : [],
      legalReference: details.legalReference || ""
    };
  }, [invoice, load.classification]);

  const routeImageSrc = useMemo(() => {
    const from = invoice?.route?.from || invoice?.form?.from || "";
    const to = invoice?.route?.to || invoice?.form?.to || "";
    if (!from || !to || from === "-" || to === "-") return "";
    return `${API_BASE_URL}/api/routes/preview-image?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  }, [invoice]);
  const normalizedRoute = useMemo(
    () => ({
      from: invoice?.route?.from || invoice?.form?.from || "",
      to: invoice?.route?.to || invoice?.form?.to || "",
      distance: Number(invoice?.route?.distance || invoice?.summary?.distanceKm || 0) || 0
    }),
    [invoice]
  );
  const invoiceTerms = useMemo(
    () => (invoice?.terms?.length ? invoice.terms : INVOICE_BRAND.terms),
    [invoice]
  );
  const transportRegulations = useMemo(
    () =>
      (invoice?.transportRegulations?.length
        ? invoice.transportRegulations
        : INVOICE_BRAND.transportRegulations),
    [invoice]
  );
  const companyTerms = useMemo(
    () => (invoice?.companyTerms?.length ? invoice.companyTerms : INVOICE_BRAND.companyTerms),
    [invoice]
  );

  const formatMeasure = (value, unit) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return "-";
    return `${numeric.toFixed(2)}${unit}`;
  };

  const hasInvoiceData = Object.keys(invoice || {}).length > 0;
  const handlePriceChange = (index, value) => {
    const numericValue = Number(value);
    setEditableBreakdown((prev) =>
      prev.map((item, idx) =>
        idx === index
          ? {
            ...item,
            value: Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : 0
          }
          : item
      )
    );
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setMessage("");

      const payload = {
        ...invoice,
        route: normalizedRoute,
        client: {
          ...(invoice?.client || {}),
          name: client.name === "Client Name" ? "" : client.name,
          company: client.company === "-" ? "" : client.company,
          email: client.email === "-" ? "" : client.email,
          phone: client.phone === "-" ? "" : client.phone,
          contact:
            client.email !== "-" ? client.email : client.phone !== "-" ? client.phone : ""
        },
        loadDetails: {
          weight: load.weight,
          width: load.width,
          height: load.height,
          machineType: load.machineType,
          machineCategory: load.machineCategory,
          trailerType: load.trailerType,
          axleConfiguration: load.axleConfiguration,
          classification: load.classification
        },
        breakdown: editableBreakdown,
        terms: invoiceTerms,
        transportRegulations,
        companyTerms,
        total: editedTotal || total,
        invoiceNumber: invoice?.invoiceNumber || "INV-" + Date.now(),
        date: new Date().toISOString()
      };

      const res = await fetch(`${API_BASE_URL}/api/jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }

      if (!res.ok) throw new Error(data?.error || "Failed to save invoice.");

      setMessageType("success");
      setMessage("Invoice saved successfully.");
    } catch (err) {
      setMessageType("error");
      setMessage(err?.message || "Unable to save invoice.");
    } finally {
      setIsSaving(false);
    }
  };
  const handleDownloadPdf = async () => {
    try {
      setIsGenerating(true);
      setMessage("");
      if (!normalizedRoute.from || !normalizedRoute.to) {
        throw new Error("Please generate a route quote first before downloading the invoice.");
      }
      await generateInvoicePDF({
        ...invoice,
        route: normalizedRoute,
        client,
        riskAssessment,
        loadDetails: {
          weight: load.weight,
          width: load.width,
          height: load.height,
          machineType: load.machineType,
          machineCategory: load.machineCategory,
          trailerType: load.trailerType,
          axleConfiguration: load.axleConfiguration,
          classification: load.classification
        },
        terms: invoiceTerms,
        transportRegulations,
        companyTerms,
        total: editedTotal || total,
        breakdown: editableBreakdown,
        routeImageSrc
      });
      setMessageType("success");
      setMessage("Invoice PDF downloaded successfully.");
    } catch (err) {
      setMessageType("error");
      setMessage(err?.message || "Failed to generate invoice PDF.");
    } finally {
      setIsGenerating(false);
    }
  };

  if (!hasInvoiceData) return <p className="empty">No invoice data available yet.</p>;

  return (
    <div className="invoice-container modern">

      {/* HEADER */}
      <div className="invoice-top modern-header">

        <div className="invoice-brand">
          <div className="invoice-logo-shell">
            <img src={logo} alt="ALIJAY Logo" className="invoice-logo" />
          </div>
          <div>
            <h1>{INVOICE_BRAND.companyName}</h1>
            <p className="tagline">{INVOICE_BRAND.tradingName}</p>
            <p className="tagline">{INVOICE_BRAND.registration}</p>
          </div>
        </div>

        <div className="invoice-meta">
          <h2>{invoice?.invoiceNumber || "N/A"}</h2>
          <p>Issued: {formatDate(invoice?.date)}</p>
          <p>Valid Until: {formatDate(validityDate)}</p>
          <span className="status-badge">
            {load.classification}
          </span>
        </div>

      </div>

      <div className="invoice-contact-bar">
        <span>{INVOICE_BRAND.address}</span>
        <span>VAT: {INVOICE_BRAND.vatNumber}</span>
        <span>Tax Code: {INVOICE_BRAND.taxCode}</span>
        <span>{INVOICE_BRAND.contactEmail}</span>
        <span>{INVOICE_BRAND.contactPhone}</span>
      </div>

      {/* DESCRIPTION */}
      <div className="invoice-card highlight">
        <h3>Service Overview</h3>
        <p>{invoice?.description || "No description available"}</p>
      </div>

      {/* CLIENT */}
      <div className="invoice-card">
        <h3>Business Details</h3>
        <div className="grid-3 modern-grid">
          <div>
            <span>Business Name</span>
            <strong>{INVOICE_BRAND.companyName}</strong>
          </div>
          <div>
            <span>VAT</span>
            <strong>{INVOICE_BRAND.vatNumber}</strong>
          </div>
          <div>
            <span>Tax Code</span>
            <strong>{INVOICE_BRAND.taxCode}</strong>
          </div>
        </div>
        <div className="route-distance">{(INVOICE_BRAND.addressLines || [INVOICE_BRAND.address]).join(", ")}</div>
      </div>

      {/* CLIENT */}
      <div className="invoice-card">
        <h3>Client Details</h3>
        <div className="grid-3 modern-grid">
          <div>
            <span>Name</span>
            <strong>{client.name}</strong>
          </div>
          <div>
            <span>Company</span>
            <strong>{client.company}</strong>
          </div>
          <div>
            <span>Email</span>
            <strong>{client.email}</strong>
          </div>
        </div>
        <div className="route-distance">Phone: {client.phone}</div>
      </div>

      {/* ROUTE */}
      <div className="invoice-card">
        <h3>Route Details</h3>

        <div className="route-modern">
          <div>
            <span>From</span>
            <strong>{normalizedRoute.from || "-"}</strong>
          </div>

          <div className="route-arrow">→</div>

          <div>
            <span>To</span>
            <strong>{normalizedRoute.to || "-"}</strong>
          </div>
        </div>

        <div className="route-distance">
          {Math.round(normalizedRoute.distance || 0)} km
        </div>
        <div className="route-distance">
          Effective Heavy Route: {Number(invoice?.pricing?.effectiveDistanceKm || normalizedRoute.distance || 0).toFixed(1)} km
        </div>
      </div>

      {/* ROUTE IMAGE */}
      <div className="invoice-card">
        <h3>Route Picture</h3>
        {routeImageSrc ? (
          <img
            className="invoice-route-image"
            src={routeImageSrc}
            alt="Route preview map"
            loading="lazy"
            onError={() => setRouteImageFailed(true)}
            onLoad={() => setRouteImageFailed(false)}
          />
        ) : (
          <p className="muted">Route picture is not available yet.</p>
        )}
        {routeImageSrc && routeImageFailed && (
          <p className="error">Route image failed to load. Check Google API key and backend route image endpoint.</p>
        )}
      </div>

      {/* LOAD */}
      <div className="invoice-card">
        <h3>Load Specifications</h3>

        <div className="grid-3 modern-grid">
          <div>
            <span>Weight</span>
            <strong>{formatMeasure(load.weight, "t")}</strong>
          </div>
          <div>
            <span>Width</span>
            <strong>{formatMeasure(load.width, "m")}</strong>
          </div>
          <div>
            <span>Height</span>
            <strong>{formatMeasure(load.height, "m")}</strong>
          </div>
          <div>
            <span>Machine Type</span>
            <strong>{load.machineType || "-"}</strong>
          </div>
          <div>
            <span>Machine Category</span>
            <strong>{load.machineCategory || "-"}</strong>
          </div>
          <div>
            <span>Trailer Type</span>
            <strong>{load.trailerType || "-"}</strong>
          </div>
          <div>
            <span>Axle Configuration</span>
            <strong>{load.axleConfiguration || "-"}</strong>
          </div>
        </div>
      </div>

      {/* COST */}
      <div className="invoice-card">
        <h3>Cost Breakdown</h3>

        {editableBreakdown.length > 0 ? (
          editableBreakdown.map((item, i) => (
            <div key={`${item.label}-${i}`} className="cost-row modern-row">
              <span>{item.label}</span>
              <div className="price-edit-wrap">
                <input
                  className="price-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={Number(item.value || 0)}
                  onChange={(event) => handlePriceChange(i, event.target.value)}
                  aria-label={`${item.label} amount`}
                />
                <strong>{format(item.value)}</strong>
              </div>
            </div>
          ))
        ) : (
          <p className="muted">No cost items available.</p>
        )}
        <div className="route-distance">
          Toll Gates: {invoice?.pricing?.tollGates ?? 0} | Toll Class Multiplier: {Number(invoice?.pricing?.tollClassMultiplier || 1).toFixed(2)}x
        </div>
      </div>

      {/* TOTAL */}
      <div className="invoice-total-modern">
        <span>Total Quotation</span>
        <h1>{format(editedTotal || total)}</h1>
      </div>

      {/* OPERATIONAL SNAPSHOT */}
      <div className="invoice-card">
        <h3>Operational Snapshot</h3>

        <div className="grid-3 modern-grid">
          <div>
            <span>Permit</span>
            <strong>
              {invoice?.compliance?.requiresPermit ? "Required" : "No"}
            </strong>
          </div>
          <div>
            <span>Escort Vehicles</span>
            <strong>{invoice?.compliance?.escorts || 0}</strong>
          </div>
          <div>
            <span>Risk Level</span>
            <strong>{riskAssessment.riskLevel.toUpperCase()}</strong>
          </div>
          <div>
            <span>Police Escort</span>
            <strong>
              {invoice?.compliance?.policeEscort ? "Yes" : "No"}
            </strong>
          </div>
          <div>
            <span>Classification</span>
            <strong>{riskAssessment.classification}</strong>
          </div>
          <div>
            <span>Risk Score</span>
            <strong>{riskAssessment.score}</strong>
          </div>
        </div>

        <ul className="notes">
          {(invoice?.complianceNotes || []).slice(0, 3).map((n, i) => (
            <li key={i}>• {n}</li>
          ))}
        </ul>
      </div>

      {/* RISK REPORT */}
      <div className="invoice-card">
        <h3>Risk Details</h3>
        {riskAssessment.reasons.length > 0 ? (
          <ul className="notes">
            {riskAssessment.reasons.slice(0, 4).map((item, idx) => (
              <li key={`reason-${idx}`}>{item}</li>
            ))}
          </ul>
        ) : (
          <p className="muted">No major risk factors identified.</p>
        )}

        <h4>Recommendations</h4>
        {riskAssessment.recommendations.length > 0 ? (
          <ul className="notes">
            {riskAssessment.recommendations.slice(0, 4).map((item, idx) => (
              <li key={`recommendation-${idx}`}>{item}</li>
            ))}
          </ul>
        ) : (
          <p className="muted">No special recommendations.</p>
        )}

        {riskAssessment.legalReference && (
          <p className="legal">{riskAssessment.legalReference}</p>
        )}
      </div>

      {/* LEGAL */}
      <div className="invoice-card">
        <h3>Legal & Terms</h3>
        <div className="legal-compact-grid">
          <div>
            <h4>Terms</h4>
            <ul>
              {invoiceTerms.slice(0, 3).map((t, i) => (
                <li key={i}>• {t}</li>
              ))}
            </ul>
          </div>
          <div>
            <h4>Transport Regulations</h4>
            <ul>
              {transportRegulations.slice(0, 3).map((t, i) => (
                <li key={`reg-${i}`}>• {t}</li>
              ))}
            </ul>
          </div>
          <div>
            <h4>Company Conditions</h4>
            <ul>
              {companyTerms.slice(0, 3).map((t, i) => (
                <li key={`company-term-${i}`}>• {t}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* ACTIONS */}
      <div className="invoice-actions modern-actions">
        <button
          className="download-btn"
          onClick={handleDownloadPdf}
          disabled={isGenerating}
        >
          {isGenerating ? "Generating PDF..." : "Download PDF"}
        </button>
        <button className="save-btn" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Invoice"}
        </button>
      </div>
      {message && (
        <p className={`invoice-message ${messageType === "error" ? "error" : "success"}`}>
          {message}
        </p>
      )}

    </div>
  );
}